// Phase 2 (footprint) thunks — reads (phase load + silent refresh), the
// mutations, and retry. Split out of polarisThunks to keep that file within the
// repo's per-file size budget; re-exported from there.
import {AnyAction, createAsyncThunk, ThunkDispatch} from "@reduxjs/toolkit";
import polarisApi from "api/polarisApi";
import {TFootprintResponse, TGenerateClusterResponse, TIncludeScope} from "api/polarisApi/types";
import type {TRootState} from "types";
import {isFootprintStateConflict} from "./footprintGeneration";
import {ensureOk, searchIdOf} from "./thunkUtils";

// ---- Footprint reads ----
//
// Every footprint GET is tagged (via fulfilled meta) with the footprint epoch
// it started under, a session-wide read sequence and the search id. The slice
// drops a GET whose epoch has moved (a mutation started or applied since it
// left), that is older than a GET already applied, or that belongs to another
// search — so a slow poll can never overwrite a newer mutation result.

export type TFootprintReadMeta = {
	footprintEpoch: number;
	footprintReadSeq: number;
	searchId: string;
};

// Monotonic for the page's lifetime; orders footprint GETs by when they left.
let footprintReadSeq = 0;

const readFootprint = async (getState: () => unknown, fallbackMessage: string) => {
	const searchId = searchIdOf(getState);

	footprintReadSeq += 1;

	// Captured before the request leaves: the epoch at departure is what
	// decides whether the answer is still current when it lands.
	const meta: TFootprintReadMeta = {
		footprintEpoch: (getState() as TRootState).polaris.footprint_epoch ?? 0,
		footprintReadSeq,
		searchId,
	};
	const data = ensureOk(await polarisApi.getFootprint(searchId), fallbackMessage);

	return {data, meta};
};

type TFootprintReadConfig = {fulfilledMeta: TFootprintReadMeta};

// The phase load (via loadPhaseData: drives the skeleton and the error panel).
export const loadFootprint = createAsyncThunk<TFootprintResponse, void, TFootprintReadConfig>(
	"polaris/loadFootprint",
	async (_, {getState, fulfillWithValue}) => {
		const {data, meta} = await readFootprint(getState, "Could not load the footprint");

		return fulfillWithValue(data, meta);
	},
);

// Silent refresh for the generation poll, SSE nudges and 409 recovery. Never
// touches loading_phase / load_error / is_working / error (see the slice), so
// it cannot flash the skeleton or swap the map for an error panel.
export const refreshFootprint = createAsyncThunk<TFootprintResponse, void, TFootprintReadConfig>(
	"polaris/refreshFootprint",
	async (_, {getState, fulfillWithValue}) => {
		const {data, meta} = await readFootprint(getState, "Could not refresh the footprint");

		return fulfillWithValue(data, meta);
	},
);

// Runs a footprint action; when the backend refuses it because the footprint
// is not in the state this UI assumed (409 footprint_generating & co.), kicks a
// silent refresh so the card catches up. The error is re-thrown (the action
// still rejects) and carries its code for callers that skip their toast.
export const refreshOnFootprintConflict = async <T>(
	dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
	run: () => Promise<T>,
): Promise<T> => {
	try {
		return await run();
	} catch (error) {
		if (isFootprintStateConflict(error)) dispatch(refreshFootprint());

		throw error;
	}
};

// Footprint mutations all answer with the full footprint state. Their pending
// and fulfilled bump the slice's footprint_epoch (see the race note above).
// The fulfilled action carries the search the request was sent for, so a
// result landing after the user switched search is dropped by the slice.

export type TFootprintMutationMeta = {searchId: string};

type TFootprintMutationConfig = {fulfilledMeta: TFootprintMutationMeta};

export const updateFootprintScope = createAsyncThunk<
	TFootprintResponse,
	TIncludeScope,
	TFootprintMutationConfig
>("polaris/updateFootprintScope", async (includeScope, {getState, dispatch, fulfillWithValue}) => {
	const searchId = searchIdOf(getState);
	const data = await refreshOnFootprintConflict(dispatch, async () =>
		ensureOk(
			await polarisApi.patchFootprint(searchId, includeScope),
			"Could not update the footprint",
		),
	);

	return fulfillWithValue(data, {searchId});
});

export const removeCluster = createAsyncThunk<TFootprintResponse, string, TFootprintMutationConfig>(
	"polaris/removeCluster",
	async (clusterId, {getState, dispatch, fulfillWithValue}) => {
		const searchId = searchIdOf(getState);
		const data = await refreshOnFootprintConflict(dispatch, async () =>
			ensureOk(
				await polarisApi.deleteCluster(searchId, clusterId),
				"Could not remove the cluster",
			),
		);

		return fulfillWithValue(data, {searchId});
	},
);

export const removeClusterCompany = createAsyncThunk<
	TFootprintResponse,
	{clusterId: string; companyName: string},
	TFootprintMutationConfig
>("polaris/removeClusterCompany", async (arg, {getState, dispatch, fulfillWithValue}) => {
	const searchId = searchIdOf(getState);
	const data = await refreshOnFootprintConflict(dispatch, async () =>
		ensureOk(
			await polarisApi.deleteClusterCompany(searchId, arg.clusterId, arg.companyName),
			"Could not remove the company",
		),
	);

	return fulfillWithValue(data, {searchId});
});

// "Add cluster": the backend drafts a cluster from the description and fills it
// with companies (~15-30 s). The answer is the whole footprint plus the new
// cluster's id; it is stored like any mutation result. Failures are toasted by
// the add form, which keeps the text (see the slice's rejected matcher).
export const addClusterFromText = createAsyncThunk<
	TGenerateClusterResponse,
	string,
	TFootprintMutationConfig
>("polaris/addClusterFromText", async (description, {getState, dispatch, fulfillWithValue}) => {
	const searchId = searchIdOf(getState);
	const data = await refreshOnFootprintConflict(dispatch, async () =>
		ensureOk(
			await polarisApi.generateCluster(searchId, description),
			"Could not add the cluster",
		),
	);

	return fulfillWithValue(data, {searchId});
});

// Re-runs a failed generation. The answer (normally pending, no clusters) is
// stored like any mutation result; the generation poll then resumes on its own.
export const retryFootprint = createAsyncThunk<TFootprintResponse, void, TFootprintMutationConfig>(
	"polaris/retryFootprint",
	async (_, {getState, dispatch, fulfillWithValue}) => {
		const searchId = searchIdOf(getState);
		const data = await refreshOnFootprintConflict(dispatch, async () =>
			ensureOk(await polarisApi.retryFootprint(searchId), "Could not retry the market map"),
		);

		return fulfillWithValue(data, {searchId});
	},
);
